import React, { Fragment, forwardRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import cx from 'classnames';

import {
    Spacer,
    Text,
    Flex,
} from '@zillow/constellation';

import { getDisplayPrice } from 'app/shared/utils/paidInclusionUtils';

import ListingActivationTerms from 'app/shared/components/ListingActivationTerms';
import SpinnerContainer from 'app/shared/components/SpinnerContainer';
import PaidInclusionConstants from 'app/shared/constants/PaidInclusionConstants';

import zGuaranteeIcon from 'app/shared/images/z-guarantee.svg';

import {
    StyledContainer,
    StyledBodyContainer,
    StyledGuaranteeIcon,
} from './StyledComponents';

import './style.scss';

const CreditCardCapture = forwardRef(({
    price,
    apiAccessToken,
    isIframeLoaded,
    isCapturing,
    isCaptured,
    // deprecated
    isEligibleForLeadGuarantee: isEligibleForLeadGuaranteeLegacy,
    onLoadIframe,
    onCleanUpIframe,
    formErrorMessage,
    displayedInSelectPaymentMethodModal,
}, ref) => {
    const { currentProperty } = useSelector(state => state.properties);

    useEffect(() => {
        if (apiAccessToken) {
            onLoadIframe(apiAccessToken);
            return onCleanUpIframe;
        }
        return () => {};
    }, [apiAccessToken, onCleanUpIframe, onLoadIframe]);

    const {
        paidInclusionSelectedProductType: selectedProductType,
        paidInclusion: {
            products,
        },
    } = currentProperty;

    // TODO: remove after we fully release feature
    const useLegacyData = products.length === 0;

    const foundIndex = products.findIndex(product => product.type === selectedProductType);
    const selectedProduct = products[foundIndex];
    const { isEligibleForLeadGuarantee } = selectedProduct || {};

    const legacyDisplayPrice = `${price} per week`;

    const displayPrice = useLegacyData
        ? legacyDisplayPrice
        : getDisplayPrice(selectedProduct);

    // TODO: remove after we fully release feature
    const isEligibleForLeadGuaranteeTemp = useLegacyData
        ? isEligibleForLeadGuaranteeLegacy
        : isEligibleForLeadGuarantee;

    const showSpinner = !isIframeLoaded || isCapturing || isCaptured;

    return (
        <StyledContainer
            ref={ref}
            className={cx('CreditCardCapture', {
                'CreditCardCapture-defaultPaymentExists': displayedInSelectPaymentMethodModal,
            })}
        >
            <StyledBodyContainer>
                {showSpinner && <SpinnerContainer />}
                {!showSpinner && !displayedInSelectPaymentMethodModal && (
                    <Fragment>
                        <Spacer marginBottom="md">
                            <Text fontType="body" data-test="renew-info">
                                {selectedProductType === PaidInclusionConstants.PRODUCT_TYPE[1] && (
                                    'Your listing will renew automatically until you deactivate it. '
                                )}
                                Your card will be charged
                                <b type={selectedProductType} data-test="display-price">{` ${displayPrice} `}</b>
                                while your listing is active on the Zillow Rental Network.
                            </Text>
                        </Spacer>

                        {isEligibleForLeadGuaranteeTemp && (
                            <Spacer marginBottom="md" data-test="lead-guarantee">
                                <Flex
                                    display="flex"
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="center"
                                >
                                    <StyledGuaranteeIcon alt="" src={zGuaranteeIcon} />
                                    <Text fontType="body">Includes Lead Guarantee</Text>
                                </Flex>
                            </Spacer>
                        )}
                    </Fragment>
                )}
                {!showSpinner && displayedInSelectPaymentMethodModal && (
                    <Spacer marginY="md">
                        <Text as="div" fontType="body" className="Text-notification">
                            This card will become the default payment method for all upcoming renewals & future listing
                            activations.
                        </Text>
                    </Spacer>
                )}
                <div
                    id={PaidInclusionConstants.CREDIT_CARD_FORM.ID_SELECTOR}
                    className={cx(
                        PaidInclusionConstants.CREDIT_CARD_FORM.CLASS_SELECTOR,
                        'fs-block',
                        {
                            hideForm: showSpinner,
                        }
                    )}
                />
                {!showSpinner && formErrorMessage && (
                    <Text fontType="body" className="Text-fail" data-test="error-text">
                        {formErrorMessage}
                    </Text>
                )}
                {!showSpinner && <ListingActivationTerms actionText="By clicking Publish listing" />}
            </StyledBodyContainer>
        </StyledContainer>
    );
});

CreditCardCapture.propTypes = {
    isIframeLoaded: PropTypes.bool.isRequired,
    isCapturing: PropTypes.bool.isRequired,
    isCaptured: PropTypes.bool.isRequired,
    isEligibleForLeadGuarantee: PropTypes.bool,
    onLoadIframe: PropTypes.func.isRequired,
    onCleanUpIframe: PropTypes.func.isRequired,
    formErrorMessage: PropTypes.string.isRequired,
    apiAccessToken: PropTypes.string.isRequired,
    displayedInSelectPaymentMethodModal: PropTypes.bool.isRequired,
};

CreditCardCapture.defaultProps = {
    isEligibleForLeadGuarantee: false,
};

export default CreditCardCapture;
